// ApprovalsPage.jsx — /approvals, the approver surface (batch 50, SB-064 interim
// slice; the list/detail two panes of the future OQ-D16 three-pane console, not a
// throwaway page). Data from
//   GET /api/approver/tickets?status&type&archived  (+ /{no} detail on expand)
//   POST /api/approver/tickets/{no}/rule            (answer + transition + ledger)
// gated server-side by the job-post approve right (admin bypass).
//
// 第六十八批更正：这段头注（与页面横幅）从第六十六批起就是**假的**——它写着
// "Rulings still travel the workspace channel — this page deliberately renders NO
// actions"，而 SB-078 L3 早已把受理/驳回/请求补充 + 批注 + 作答做进了 AgentReviewPanel。
// **界面为一个不存在的缺席背书**：与 DP-6 同型、方向相反——DP-6 是宣称一个不存在的
// 行为存在，这里是宣称一个存在的行为不存在，而后者一样会让人不去用它。
// 需求方第六十八批的原话是"我目前还不能在页面中……请确保在页面上审批 ticket 已基本
// 能用"——**它两批之前就能用了，是这行字在劝人别用。**
import { Fragment, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { fetchAllPages, pagingNotice } from '../api/paging.mjs';
import AgentReviewPanel from '../components/AgentReviewPanel.jsx';
import DataTable from '../components/DataTable.jsx';
import GroupChip from '../components/GroupChip.jsx';
import HelpLink from '../components/HelpLink.jsx';
import TicketDetailView, { fmtTime } from '../components/TicketDetailView.jsx';
import TicketStatusChip from '../components/TicketStatusChip.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import { questionBadgeCount, reviewBadge } from './agentReview.mjs';
import { ASSIGNED_STATUSES, STATUS_ORDER, groupByStatus } from './ticketMeta.mjs';

// Collapse row wrapper kept local — the table shell mirrors MyTicketsPage on
// purpose (same reading habits), with a submitter column added.
import { Collapse } from '@mui/material';

// SB-190（第二百四十七批）：刻意不含 'assigned'——审批队列按机器排除 ASK-
// （routes_approver.cpp 的 exclude_machine = Assigned），一个点了必空的筛选项比没有更坏；
// assignedSurface.test 钉住「= TICKET_TYPES 去掉 assigned」。若某天 ASK 进了队列，
// typeLabel 也只会印原码而不会崩——但那一天应先改后端与本清单，再改测试。
const TYPE_ORDER = ['spec_change', 'bug', 'incident', 'maintenance', 'tech_debt', 'self_intro', 'asset'];
// 同一条理由落在状态筛选上（复审）：STATUS_ORDER 自第二百四十五批起含 A 机的三个状态
// （assigned／responded／closed），而这一页永远查不到 A 机的单——照单全列就是三个点了必空的
// 选项。按注册表**减**，不另抄一份清单（assignedSurface.test 钉住）。
const APPROVER_STATUS_ORDER = STATUS_ORDER.filter((s) => !ASSIGNED_STATUSES.includes(s));

// ── SB-159（第三百八十二批，需求方实撞）：队列取到底 ─────────────────────────────────
// 「状态=全部、类型=全部」时，仍开着的事故单（INC-000010/011）只在明确选「事故」时才看得见：
// 此前这一页只发一次请求，服务端缺省一页 200、按 id 升序、应答里没有任何一个字说「还有」，于是
// 最新的那些掉在第一页之外；窄筛选装得进一页，它们才在那时现身。
// 这一页把全集**按状态分组**——给它一个翻页器或「最近 N 张」仍然会藏单，所以它取到底
// （fetchAllPages 沿 nextCursor 一页页取，只在服务端说 hasMore 时继续），并把张数摆在页上；
// 撞页数上限、或张数与服务端 total 对不上，都有一条看得见的提示——**静默是唯一不许的结局**。
// 200 ＝ 服务端缺省页长：今天约 255 张的队列线上真的走两页。50 页是防御护栏（一万张）。
const APPROVER_PAGE_LIMIT = 200;
const APPROVER_MAX_PAGES = 50;

export default function ApprovalsPage() {
  const t = useT();
  const { lang } = useLang();

  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [archived, setArchived] = useState(false);
  const [openNo, setOpenNo] = useState(null);
  // 裁完之后强制重挂详情组件（它按 key 重取）。见下方 onRule。
  const [reloadKey, setReloadKey] = useState(0);

  // 任何一页失败 ⇒ 整次加载失败（下面那条 error 分支 + 重试）；裁完 list.reload() 会重新走完全部页。
  const list = useLoad(
    () =>
      fetchAllPages(
        (p) =>
          api.approverTickets({
            status: statusFilter || undefined,
            type: typeFilter || undefined,
            archived: archived ? 'true' : undefined,
            limit: p.limit,
            after: p.after,
          }),
        { rowsKey: 'tickets', pageLimit: APPROVER_PAGE_LIMIT, maxPages: APPROVER_MAX_PAGES },
      ),
    [statusFilter, typeFilter, archived, lang],
  );
  const tickets = Array.isArray(list.data?.rows) ? list.data.rows : [];
  const groups = groupByStatus(tickets);
  // 撞上限／旧后端满页／张数与 total 对不上 ⇒ 一条提示；取全了 ⇒ null。三句话是这一页自己的
  // （approvals.paging.*：量词「张」，drift 那句的「点重试重新取全」在这一页成立——它一页页取到底）。
  const notice = list.status === 'ready'
    ? pagingNotice(list.data, { pageLimit: APPROVER_PAGE_LIMIT, ns: 'approvals.paging' })
    : null;

  const typeLabel = (ty) => (t(`ticketType.${ty}`) === `ticketType.${ty}` ? ty : t(`ticketType.${ty}`));

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 1 }}
      >
        <Typography sx={{ fontWeight: 700 }} variant="h5" component="h1">
          {t('approvals.title')}
          {/* 审批面没有独立章：它是"提交之后"那条线的处理端（HELP_PAGE_MAP 同款注）。 */}
          <HelpLink section="lifecycle" />
        </Typography>
        {/* useFlexGap：Stack 的 spacing 默认走 margin，折行后第二行会贴死且带 16px 假缩进 */}
        <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={2} useFlexGap>
          <TextField
            select
            size="small"
            label={t('tickets.filter.status')}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('tickets.filter.all')}</MenuItem>
            {APPROVER_STATUS_ORDER.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`ticketStatus.${s}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('approvals.filter.type')}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">{t('tickets.filter.all')}</MenuItem>
            {TYPE_ORDER.map((ty) => (
              <MenuItem key={ty} value={ty}>
                {typeLabel(ty)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={archived} onChange={(e) => setArchived(e.target.checked)} />}
            label={t('tickets.filter.archived')}
          />
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('approvals.readonlyNote')}
      </Typography>

      {list.status === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.status === 'error' ? (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>
            {t(errKey(list.error, { fallback: 'approvals.error' }))}
          </Alert>
          <Button size="small" onClick={list.reload}>
            {t('common.retry')}
          </Button>
        </Box>
      ) : (
        <>
          {/* SB-159：取数结局的那一句。空列表上同样要说——服务端 total 说有、列表却空着，
              正是「取不到」长得像「没有」的那一幕（口径 3）。对不上（drift）给重试。 */}
          {notice ? (
            <Alert
              severity={notice.severity}
              data-paging-notice={notice.key}
              sx={{ mb: 2 }}
              action={notice.kind === 'drift' ? (
                <Button color="inherit" size="small" onClick={list.reload}>
                  {t('common.retry')}
                </Button>
              ) : undefined}
            >
              {t(notice.key, notice.params)}
            </Alert>
          ) : null}
          {tickets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {statusFilter || typeFilter || archived ? t('approvals.emptyFiltered') : t('approvals.empty')}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {/* SB-159：这一页列了几张——与服务端 total 对得上时它就是全集（browser-check 的
              approvals-complete 探针读这个属性，对 /api/approver/tickets 的 total）。 */}
          <Typography variant="body2" color="text.secondary" data-approvals-total={tickets.length}>
            {t('approvals.total', { n: tickets.length })}
          </Typography>
          {groups.map(({ status, rows }) => (
            <Box key={status}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <TicketStatusChip status={status} />
                <Typography variant="body2" color="text.secondary">
                  {t('tickets.count', { n: rows.length })}
                </Typography>
              </Stack>
              <DataTable minWidth={640}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('tickets.col.no')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('tickets.col.type')}</TableCell>
                      <TableCell>{t('tickets.col.title')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('approvals.col.submitter')}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('tickets.col.updated')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <Fragment key={row.no}>
                        <TableRow
                          hover
                          onClick={() => setOpenNo(openNo === row.no ? null : row.no)}
                          sx={{ cursor: 'pointer', '& > td': { borderBottom: 'none' } }}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace' }}>
                            {row.no}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{typeLabel(row.type)}</TableCell>
                          <TableCell>
                            {/* flexWrap：窄视口下徽标掉行而不是截断——SB-078 的「N 个问题」
                                徽标被省略号吃掉，等于徽标不存在 */}
                            <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1} useFlexGap>
                              <span>{row.title}</span>
                              {/* 第一百零三批：组徽标一律显示——多项目制下组是裁决
                                  偏移的界面因子，审批人裁决前必须一眼看到，不按
                                  组数或「行组=当前组」隐藏（GroupChip 头注）。 */}
                              <GroupChip groupId={row.projectGroupId} />
                              {row.archived ? (
                                <Chip size="small" variant="outlined" label={t('tickets.archivedChip')} />
                              ) : null}
                              {/* SB-078 L2：不点开也知道哪几张有问题在等。0 不显示徽标。 */}
                              {questionBadgeCount(row) > 0 ? (
                                <Chip
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  label={t('agentReview.badge', { n: questionBadgeCount(row) })}
                                />
                              ) : null}
                              {/* 第一百一十批 B①：预读出事了的那几张。判据（哪些状态
                                  值得占像素、什么颜色）在 agentReview.mjs 的 reviewBadge
                                  ——「一切正常」不该占像素，同 questionBadgeCount 的 0
                                  不显示。null ⇒ 整枚不渲染。 */}
                              {reviewBadge(row) ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={reviewBadge(row).color}
                                  label={t(reviewBadge(row).key)}
                                />
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {/* SB-147：并排邮箱（详情头同形）——审批面认错提交人
                                的代价是把裁决落到别人头上。 */}
                            {row.submitter
                              ? (row.submitter.displayName
                                  ? `${row.submitter.displayName} <${row.submitter.email}>`
                                  : row.submitter.email)
                              : '—'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtTime(row.updatedAtMs, lang)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0 }}>
                            {/* sticky+maxWidth：详情面板钉在滚动窗口左缘，不随表格 minWidth
                                被撑宽（同 MyTicketsPage，64px = Container 16×2 + 单元格 16×2） */}
                            <Box sx={{ position: 'sticky', left: 0, maxWidth: 'calc(100vw - 64px)' }}>
                            <Collapse in={openNo === row.no} timeout="auto" unmountOnExit>
                              <TicketDetailView
                                no={row.no}
                                load={() => api.approverTicketDetail(row.no)}
                                attachmentUrl={api.approverAttachmentUrl}
                                authorLabels={{
                                  submitter: 'approvals.author.submitter',
                                  handler: 'approvals.author.handler',
                                }}
                                key={`${row.no}:${reloadKey}`}
                                header={(d) => (
                                  <>
                                    <Typography variant="caption" color="text.secondary">
                                      {t('approvals.detail.submitter')}:{' '}
                                      {d.submitter
                                        ? `${d.submitter.displayName} <${d.submitter.email}>`
                                        : '—'}
                                      {d.targetDocKey ? ` · ${t('approvals.detail.target')}: ${d.targetDocKey}` : ''}
                                    </Typography>
                                    {/* SB-078 L2：agent 预读放在正文**之前**——审批人先看到
                                        「有什么要你定的」，再读工单。建议本身默认折叠。
                                        L3 起它还能作答：答完即转态（需求方本批裁）。 */}
                                    <AgentReviewPanel
                                      raw={d.agentReview}
                                      submitLang={d.submitLang}
                                      rulings={d.rulings}
                                      /* 第一百一十批 B①：三态与逃生口。**永不为 null**
                                         的投影，旧后端缺这两个键时面板自己退回今天的
                                         行为（什么都不说、不给重跑按钮）。 */
                                      status={d.agentReviewStatus}
                                      history={d.agentReviewHistory}
                                      no={row.no}
                                      onRule={async (body) => {
                                        const r = await api.approverRule(row.no, body);
                                        // 裁完整页重取：工单状态、台账、通知计数三处都变了，
                                        // 只刷面板会让状态列停在旧值上——而那正是
                                        //「我明明已经裁了」的成因。
                                        setReloadKey((k) => k + 1);
                                        list.reload();
                                        return r;   // 面板要拿 derivedNo 回显
                                      }}
                                      onRerun={async (body) => {
                                        const r = await api.approverRequeueReview(row.no, body);
                                        // 只重取**列表**，**刻意不换详情的 key**。
                                        // 两个理由，方向一致：
                                        //  · 列表要刷——新一轮排进去之后这张单的
                                        //    reviewState 变成 queued，而列表那枚徽标
                                        //    正是"别急着裁，AI 还在读"的唯一提示；
                                        //  · 详情**不能**重挂——重挂会把面板整块换掉，
                                        //    连同刚给出的那张"已排队第 N 轮"的收据一起
                                        //    消失。一张在同一帧里就没了的收据不是收据，
                                        //    而这一步之后页面上确实没有别的变化可看
                                        //    （新一轮跑完之前内容仍是上一轮的——收据
                                        //    那句话说的就是这件事）。
                                        list.reload();
                                        return r;
                                      }}
                                    />
                                  </>
                                )}
                              />
                            </Collapse>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
              </DataTable>
            </Box>
          ))}
        </Stack>
          )}
        </>
      )}
    </Box>
  );
}
